import React from 'react';
import { 
  Box, 
  TextField, 
  Typography, 
  Card,
  Grid,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SectionWrapper from './SectionWrapper';

const LatexEditor = ({ 
  title,
  templated, 
  edited, 
  onEdit,
  disabled = false,
  modifierKey = 'Ctrl',
  showShortcut = false,
  onPaste
}) => {
  const theme = useTheme();

  if (!templated) return null;

  const renderMcqQuestionEditor = () => (
    <Box sx={{ height: '100%', display: 'flex', p: 2, pt: 3 }}>
      <TextField
        fullWidth
        multiline
        label="Question"
        value={edited.question_content || templated.question_content}
        onChange={(e) => onEdit('question_content', e.target.value)}
        onPaste={onPaste}
        variant="outlined"
        disabled={disabled}
        sx={{
          flex: 1,
          '& .MuiInputBase-root': {
            height: '100%',
            alignItems: 'flex-start',
          },
          '& .MuiInputBase-input': {
            height: '100% !important',
            overflow: 'auto !important',
          },
        }}
      />
    </Box>
  );

  const renderMcqOptionsEditor = () => (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'auto',
      p: 2,
      pt: 3
    }}>
      <Grid container spacing={2} sx={{ flex: 1 }}>
        {[
          { label: 'Option A', field: 'option_a' },
          { label: 'Option B', field: 'option_b' },
          { label: 'Option C', field: 'option_c' },
          { label: 'Option D', field: 'option_d' }
        ].map((option) => (
          <Grid item xs={6} key={option.field} sx={{ display: 'flex' }}>
            <TextField
              fullWidth
              multiline
              minRows={1}
              label={option.label}
              value={edited[option.field] || templated[option.field]}
              onChange={(e) => onEdit(option.field, e.target.value)}
              onPaste={onPaste}
              variant="outlined"
              disabled={disabled}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start',
                  flex: 1,
                },
                '& .MuiInputBase-input': {
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'break-word',
                  lineHeight: 1.5,
                },
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderPartContentEditor = () => (
    <Box sx={{ height: '100%', overflow: 'auto', p: 2, pt: 3 }}>
      {/* Main question content first */}
      <TextField
        fullWidth
        multiline
        minRows={3}
        label="Main Question"
        value={edited.question_content || templated.question_content}
        onChange={(e) => onEdit('question_content', e.target.value)}
        onPaste={onPaste}
        variant="outlined"
        disabled={disabled}
        sx={{ mb: 3 }}
      />
      
      {/* Part content editors */}
      {templated.parts.map((part) => (
        <Card 
          key={part.id} 
          sx={{ 
            mb: 2, 
            p: 2, 
            backgroundColor: alpha(theme.palette.grey[100], 0.5) 
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            Part {part.part_number}
          </Typography>
          
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={`Part ${part.part_number} Content`}
            value={edited[`part_${part.id}_content`] || part.part_content}
            onChange={(e) => onEdit(`part_${part.id}_content`, e.target.value)}
            onPaste={onPaste}
            variant="outlined"
            disabled={disabled}
          />
          
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            {part.part_marks} mark{part.part_marks !== 1 ? 's' : ''}
          </Typography>
        </Card>
      ))}
    </Box>
  );

  const renderPartAnswersEditor = () => (
    <Box sx={{ height: '100%', overflow: 'auto', p: 2, pt: 3 }}>
      {templated.parts.map((part) => (
        <Card 
          key={part.id} 
          sx={{ 
            mb: 2, 
            p: 2, 
            backgroundColor: alpha(theme.palette.grey[100], 0.5) 
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            Part {part.part_number} Answer
          </Typography>
          
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={`Part ${part.part_number} Answer`}
            value={edited[`part_${part.id}_answer`] || part.part_answer}
            onChange={(e) => onEdit(`part_${part.id}_answer`, e.target.value)}
            onPaste={onPaste}
            variant="outlined"
            disabled={disabled}
          />
          
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            {part.part_marks} mark{part.part_marks !== 1 ? 's' : ''}
          </Typography>
        </Card>
      ))}
    </Box>
  );

  const renderSimpleExtendedEditor = () => (
    <Box sx={{ height: '100%', display: 'flex', p: 2, pt: 3 }}>
      <TextField
        fullWidth
        multiline
        label="Solution"
        value={edited.solution || templated.solution || ''}
        onChange={(e) => onEdit('solution', e.target.value)}
        onPaste={onPaste}
        variant="outlined"
        disabled={disabled}
        sx={{
          flex: 1,
          '& .MuiInputBase-root': {
            height: '100%',
            alignItems: 'flex-start',
          },
          '& .MuiInputBase-input': {
            height: '100% !important',
            overflow: 'auto !important',
          },
        }}
      />
    </Box>
  );

  const renderQuestionEditor = () => {
    if (templated.type === 'mcq') {
      return renderMcqQuestionEditor();
    }

    // Extended question with parts
    if (templated.has_parts && templated.parts && templated.parts.length > 0) {
      return renderPartContentEditor();
    }

    // Simple extended question
    return (
      <Box sx={{ height: '100%', display: 'flex', p: 2, pt: 3 }}>
        <TextField
          fullWidth
          multiline
          label="Question"
          value={edited.question_content || templated.question_content}
          onChange={(e) => onEdit('question_content', e.target.value)}
          onPaste={onPaste}
          variant="outlined"
          disabled={disabled}
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              height: '100%',
              alignItems: 'flex-start',
            },
            '& .MuiInputBase-input': {
              height: '100% !important',
              overflow: 'auto !important',
            },
          }}
        />
      </Box>
    );
  };

  const renderAnswerEditor = () => {
    if (templated.type === 'mcq') {
      return renderMcqOptionsEditor(); // MCQ now shows options in answer section
    }

    if (templated.has_parts && templated.parts && templated.parts.length > 0) {
      return renderPartAnswersEditor();
    }

    return renderSimpleExtendedEditor();
  };

  // Determine what to render based on title
  const isQuestionSection = title === 'Question LaTeX';
  
  // Determine the keyboard shortcut to display
  const getShortcutText = () => {
    if (!showShortcut) return '';
    return isQuestionSection ? ` (${modifierKey}+Q)` : ` (${modifierKey}+A)`;
  };

  return (
    <SectionWrapper title={`${title}${getShortcutText()}`}>
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0, // Important for flex children
        overflow: 'hidden', // Prevent overflow from breaking layout
      }}>
        {isQuestionSection ? renderQuestionEditor() : renderAnswerEditor()}
      </Box>
    </SectionWrapper>
  );
};

export default LatexEditor;